import { css } from "@emotion/react";
import * as React from "react";
import { useSelector } from "react-redux";

import { Delete } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, ModalPortal, Text, Tooltip } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import Role from "constants/role";
import { useAttachmentDeletion } from "hooks/use-attachment-deletion";
import { IAttachment } from "interfaces/entities/ticket";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

export const buttonStyleWithTransition = css`
  background: none;
  border: none;
  opacity: 0;
  transition: all 0.2s;
  padding: 0;
  margin-left: 4px;
  li:hover & {
    opacity: 1;
  }
`;

const disabledIconStyle = css`
  svg {
    color: ${withVar(DesignToken.ContentBasicDisabled)};
    vertical-align: bottom;
  }
`;

interface ITicketAttachmentProps {
  attachment: IAttachment;
  ticketId: string;
  idx: number;
}

export const TicketAttachment: React.FC<ITicketAttachmentProps> = ({ attachment, ticketId, idx }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { mutate } = useAttachmentDeletion();

  const handleDeleteButtonClicked = (): void => {
    setIsModalOpen(true);
  };
  const isAdmin = useSelector(selectLoggedInAgentPermission) === Role.Owner;
  const handleConfirmDeletion = (): void => {
    mutate({ eventId: attachment.eventID, attachmentId: idx, ticketId });
    setIsModalOpen(false);
  };
  return (
    <li key={`${attachment.url}`}>
      {attachment.removedAt ? (
        <span data-testid={`attachment-deleted-${attachment.eventID}-${idx}`}>
          <s>{attachment.name}</s> (deleted)
        </span>
      ) : (
        <Flex alignItems="center">
          <a
            href={attachment.url}
            target="_blank"
            rel="nofollow noreferrer noopener"
            data-testid={`attachment-${attachment.eventID}-${idx}-${attachment.name}`}
          >
            {attachment.name}
          </a>
          {isAdmin &&
            (attachment.external ? (
              <Tooltip
                placement="right"
                triggerRenderer={() => (
                  <Button
                    icon={<Icon source={Delete} css={disabledIconStyle} />}
                    data-testid={`attachment-delete-${attachment.eventID}-${idx}`}
                    kind="plain"
                    size="xcompact"
                    disabled
                    onClick={handleDeleteButtonClicked}
                    css={buttonStyleWithTransition}
                  />
                )}
              >
                <Text>You can’t delete attachments from signatures and canned responses.</Text>
              </Tooltip>
            ) : (
              <Button
                icon={<Icon source={Delete} />}
                data-testid={`attachment-delete-${attachment.eventID}-${idx}`}
                kind="plain"
                size="xcompact"
                onClick={handleDeleteButtonClicked}
                css={buttonStyleWithTransition}
              />
            ))}
        </Flex>
      )}
      {isModalOpen && (
        <ModalPortal zIndex={StackingContextLevel.Modal}>
          <ConfirmActionModal
            heading="Delete attachment?"
            primaryAction={{
              dataTestId: "Delete attachment",
              label: "Delete attachment",
              onClick: handleConfirmDeletion,
              actionType: "destructive",
            }}
            secondaryAction={{
              label: "Cancel",
              onClick: () => setIsModalOpen(false),
            }}
            onClose={() => setIsModalOpen(false)}
            data-testid="attachment-delete-modal"
          >
            If you delete this attachment, it&apos;ll be irreversibly removed from HelpDesk and no longer accessible in
            the ticket details.
          </ConfirmActionModal>
        </ModalPortal>
      )}
    </li>
  );
};
